import api from "./api";

const getTeams = (search) => {
  return api
    .get("/state_set/data", {
      params: {
        stateSetId: "Team",
        ...search,
      },
    })
    .then((response) => {
      return response.data;
    });
};

const getTeam = (id) => {
  return api
    .get(`/state_set/data/Team/${id}`)
    .then((response) => {
      return response.data;
    });
};

const getTeamUi = () => {
  return api
    .get("/state_set/ui/Team")
    .then((response) => {
      return response.data;
    });
};

const createTeam = (team) => {
  return api
    .post("/state_set/data", {
      stateSetId: "Team",
      ...team,
    })
    .then((response) => {
      clearTeamCache();
      return response.data;
    });
};

const updateTeam = (team) => {
  return api
    .patch("/state_set/data", {
      stateSetId: "Team",
      ...team,
    })
    .then((response) => {
      clearTeamCache();
      return response.data;
    });
};

const deleteTeam = (id) => {
  return api
    .delete(`/state_set/data/Team/${id}`)
    .then((response) => {
      clearTeamCache();
      return response.data;
    });
};

// Cache for contributors and roles data with 2-minute expiry
let contributorsCache = null;
let contributorsCacheTime = 0;
let teamRolesCache = null;
let teamRolesCacheTime = 0;
const CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

const fetchTeamRelatedData = async () => {
  const now = Date.now();

  // Fetch contributors if cache is expired or empty
  if (!contributorsCache || now - contributorsCacheTime > CACHE_DURATION) {
    const contributorsResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "Contributor",
        page: 0,
        pageSize: 99999,
      },
    });
    contributorsCache = contributorsResponse.data.list || [];
    contributorsCacheTime = now;
  }

  // Fetch team roles if cache is expired or empty
  if (!teamRolesCache || now - teamRolesCacheTime > CACHE_DURATION) {
    const rolesResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "TeamRoles",
        page: 0,
        pageSize: 99999,
      },
    });
    teamRolesCache = rolesResponse.data.list || [];
    teamRolesCacheTime = now;
  }

  return { contributors: contributorsCache, teamRoles: teamRolesCache };
};

const getTeamMembers = async (teamId) => {
  try {
    const { contributors, teamRoles } = await fetchTeamRelatedData();

    // Get the team data
    const teamResponse = await api.get(`/state_set/data/Team/${teamId}`);
    const team = teamResponse.data;

    if (!team.team_role || !Array.isArray(team.team_role)) {
      return [];
    }

    // Create a map of contributor ID to contributor details
    // Map by _id, id, and contributor_id since the reference might use any of these
    const contributorMap = {};
    contributors.forEach((contributor) => {
      const details = {
        // Contributor uses 'name' field, not 'full_name'
        fullName: contributor.name,
        email: contributor.email,
      };
      if (contributor._id) {
        contributorMap[contributor._id] = details;
      }
      if (contributor.id && contributor.id !== contributor._id) {
        contributorMap[contributor.id] = details;
      }
      if (contributor.contributor_id) {
        contributorMap[contributor.contributor_id] = details;
      }
    });

    // Create a map of role ID to role details
    // Map both _id and id since the reference might use either
    const roleMap = {};
    teamRoles.forEach((role) => {
      const details = {
        name: role.name,
        description: role.description,
      };
      if (role._id) {
        roleMap[role._id] = details;
      }
      if (role.id && role.id !== role._id) {
        roleMap[role.id] = details;
      }
    });

    const members = [];

    team.team_role.forEach((roleEntry) => {
      const contributorId = roleEntry.contributor;
      const roleId = roleEntry._id;

      const contributorDetails = contributorMap[contributorId] || {
        fullName: "Unknown Contributor",
        email: "",
      };
      const roleDetails = roleMap[roleId] || {
        name: "Unknown Role",
        description: "",
      };

      members.push({
        contributorId,
        contributorName: contributorDetails.fullName,
        contributorEmail: contributorDetails.email,
        roleId,
        roleName: roleDetails.name,
        roleDescription: roleDetails.description,
      });
    });

    return members;
  } catch (error) {
    throw error;
  }
};

// Get all team members for all teams at once (more efficient than individual calls)
const getAllTeamMembers = async (teams) => {
  try {
    const { contributors, teamRoles } = await fetchTeamRelatedData();

    // Create a map of contributor ID to contributor details
    // Map by _id, id, and contributor_id since the reference might use any of these
    const contributorMap = {};
    contributors.forEach((contributor) => {
      const details = {
        // Contributor uses 'name' field, not 'full_name'
        fullName: contributor.name,
        email: contributor.email,
      };
      // Map by _id
      if (contributor._id) {
        contributorMap[contributor._id] = details;
      }
      // Also map by id if different
      if (contributor.id && contributor.id !== contributor._id) {
        contributorMap[contributor.id] = details;
      }
      // Also map by contributor_id if present
      if (contributor.contributor_id) {
        contributorMap[contributor.contributor_id] = details;
      }
    });

    // Create a map of role ID to role details
    // Map both _id and id since the reference might use either
    const roleMap = {};
    teamRoles.forEach((role) => {
      const details = {
        name: role.name,
        description: role.description,
      };
      // Map by _id
      if (role._id) {
        roleMap[role._id] = details;
      }
      // Also map by id if different
      if (role.id && role.id !== role._id) {
        roleMap[role.id] = details;
      }
    });

    const membersMap = {};

    teams.forEach((team) => {
      // Use id or _id, whichever is available
      const teamId = team.id || team._id;
      membersMap[teamId] = [];

      if (team.team_role && Array.isArray(team.team_role)) {
        team.team_role.forEach((roleEntry) => {
          const contributorId = roleEntry.contributor;
          const roleId = roleEntry._id || roleEntry.id;

          const contributorDetails = contributorMap[contributorId] || {
            fullName: "Unknown Contributor",
            email: "",
          };
          const roleDetails = roleMap[roleId] || {
            name: "Unknown Role",
            description: "",
          };

          membersMap[teamId].push({
            contributorId,
            contributorName: contributorDetails.fullName,
            contributorEmail: contributorDetails.email,
            roleId,
            roleName: roleDetails.name,
            roleDescription: roleDetails.description,
          });
        });
      }
    });

    return membersMap;
  } catch (error) {
    return {};
  }
};

// Batch calculate member counts for all teams
const getAllTeamMemberCounts = async () => {
  try {
    const teamsResponse = await api.get("/state_set/data", {
      params: {
        stateSetId: "Team",
        page: 0,
        pageSize: 99999,
      },
    });
    const teams = teamsResponse.data.list || [];

    const counts = {};

    teams.forEach((team) => {
      const teamId = team._id;
      const memberCount =
        team.team_role && Array.isArray(team.team_role)
          ? team.team_role.length
          : 0;
      counts[teamId] = memberCount;
    });

    return counts;
  } catch (error) {
    return {};
  }
};

// Clear all caches
const clearTeamCache = () => {
  contributorsCache = null;
  contributorsCacheTime = 0;
  teamRolesCache = null;
  teamRolesCacheTime = 0;
};

const TeamService = {
  getTeams,
  getTeam,
  getTeamUi,
  createTeam,
  updateTeam,
  deleteTeam,
  getTeamMembers,
  getAllTeamMembers,
  getAllTeamMemberCounts,
  clearTeamCache,
};

export default TeamService;
